import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../service/firebase";
import { fetchRelationships } from "../../../service/friendService";
import { inviteUserToTurma } from "../../../service/turmaService";
import "./TurmaConvites.css";

const TurmaConvites = () => {
  const { id } = useParams();
  const invitationLink = useMemo(
    () => `${window.location.origin}/convite/turma/${id}`,
    [id]
  );
  const { user } = useAuth();
  const navigate = useNavigate();

  const [turma, setTurma] = useState(null);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [copyLabel, setCopyLabel] = useState("Copiar link");
  const [invitingId, setInvitingId] = useState(null);
  const turmaStatus = typeof turma?.status === "number" ? turma.status : 1;
  const turmaAtiva = turmaStatus === 1;

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        const turmaDoc = await getDoc(doc(db, "turmas", id));
        if (!turmaDoc.exists()) {
          setError("Turma não encontrada.");
          return;
        }
        setTurma({ id, ...turmaDoc.data() });
        const membershipSnapshot = await getDocs(
          query(
            collection(db, "jogador_turma"),
            where("id_turma", "==", id),
            where("id_usuario", "==", user.uid)
          )
        );
        if (membershipSnapshot.empty || !membershipSnapshot.docs[0].data().admin) {
          setError("Você não possui permissão para convidar jogadores.");
          return;
        }
        const relationships = await fetchRelationships(user.uid);
        const activeFriends = relationships
          .filter((rel) => rel.status === "ativo")
          .map((rel) => rel.otherUser);
        setFriends(activeFriends);
      } catch (err) {
        console.error("Erro ao carregar dados da turma", err);
        setError("Não foi possível carregar os dados para convites.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, user]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopyLabel("Copiado!");
      setTimeout(() => setCopyLabel("Copiar link"), 2000);
    } catch (err) {
      console.error("Erro ao copiar link", err);
    }
  };

  const handleInvite = async (friend) => {
    if (!turma || !user) return;
    if (!turmaAtiva) {
      setError("A turma não está ativa para receber novos convites.");
      return;
    }
    setInvitingId(friend.id);
    setMessage("");
    setError("");
    try {
      await inviteUserToTurma({
        turmaId: turma.id,
        turmaNome: turma.nome,
        invitedUser: friend,
        inviter: user,
      });
      setMessage(`Convite enviado para ${friend.nome}.`);
    } catch (err) {
      console.error("Erro ao convidar", err);
      setError(err.message || "Não foi possível enviar o convite.");
    } finally {
      setInvitingId(null);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card convites-card">
            <div className="topo-page">
              <strong># Convidar Jogadores</strong>
            </div>
            {turma && (
              <p className="convites-description">
                Compartilhe o link ou convide amigos para a turma{" "}
                <strong>{turma.nome}</strong>.
              </p>
            )}
            {message && <p className="friends-success">{message}</p>}
            {error && (
              <p className="friends-error" role="alert">
                {error}
              </p>
            )}

            <section className="convites-section">
              <h3>Compartilhar link direto</h3>
              <div className="convites-link-box">
                <input type="text" value={invitationLink} readOnly />
                <button type="button" className="btn-default" onClick={handleCopy}>
                  {copyLabel}
                </button>
              </div>
            </section>

            <section className="convites-section">
              <h3>Amigos</h3>
              {!turmaAtiva && (
                <p className="friends-error">
                  A turma está {turmaStatus === 2 ? "encerrada" : "inativa"} e não aceita novos convites.
                </p>
              )}
              {loading ? (
                <span>Carregando...</span>
              ) : friends.length === 0 ? (
                <p>
                  Você ainda não possui amigos ativos.{" "}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate("/amigos")}
                  >
                    Gerenciar amigos
                  </button>
                </p>
              ) : (
                <div className="friends-list">
                  {friends.map((friend) => (
                    <div key={friend.id} className="friend-item">
                      <span>{friend.nome}</span>
                      <button
                        type="button"
                        className="btn-default"
                        onClick={() => handleInvite(friend)}
                        disabled={invitingId === friend.id || !turmaAtiva}
                      >
                        {invitingId === friend.id ? "Enviando..." : "Convidar"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TurmaConvites;
